// src/components/Supplier/SupplierProfileModal.jsx
import React, { useState } from 'react';
import { X, Building2, Save, Upload, AlertCircle, CheckCircle } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.lbnhyderabad.com';

export default function SupplierProfileModal({ supplier, onClose, onUpdateSuccess }) {
  const [contactPerson, setContactPerson] = useState(supplier.contact_person || '');
  const [mobile, setMobile] = useState(supplier.mobile || '');
  const [catalogSummary, setCatalogSummary] = useState(supplier.product_catalog_summary || '');
  const [websiteUrl, setWebsiteUrl] = useState(supplier.company_website_url || '');
  const [logoFile, setLogoFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMsg({ type: '', text: '' });

    try {
      const formData = new FormData();
      formData.append('contactPerson', contactPerson);
      formData.append('mobile', mobile);
      formData.append('catalogSummary', catalogSummary);
      formData.append('websiteUrl', websiteUrl);
      if (logoFile) formData.append('logo', logoFile);

      const res = await fetch(`${API_BASE_URL}/api/supplier/${supplier.id}/profile`, {
        method: 'PUT',
        body: formData
      });
      const data = await res.json();

      if (!res.ok || !data.success) throw new Error(data.message || 'Profile update failed');

      setMsg({ type: 'success', text: data.message });
      onUpdateSuccess(data.supplier);
      setTimeout(onClose, 1000);
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(2, 44, 34, 0.75)',
      backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center',
      justifyContent: 'center', zIndex: 2000, padding: '16px'
    }}>
      <div style={{
        background: '#ffffff', borderRadius: '16px', maxWidth: '540px',
        width: '100%', maxHeight: '90vh', overflowY: 'auto',
        border: '1.5px solid #10b981', padding: '24px', boxSizing: 'border-box'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px' }}>
          <div>
            <h3 style={{ margin: 0, color: '#064e3b', fontSize: '1.2rem', fontFamily: 'Playfair Display, serif' }}>
              Corporate Profile & Catalog Settings
            </h3>
            <span style={{ fontSize: '0.72rem', color: '#64748b' }}>Edit operational contact details and customer offerings</span>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#64748b' }}>
            <X size={18} />
          </button>
        </div>

        {msg.text && (
          <div style={{
            padding: '10px', borderRadius: '8px', marginBottom: '14px', fontSize: '0.78rem',
            background: msg.type === 'error' ? '#fee2e2' : '#dcfce7',
            color: msg.type === 'error' ? '#991b1b' : '#166534',
            display: 'flex', alignItems: 'center', gap: '8px'
          }}>
            {msg.type === 'error' ? <AlertCircle size={15} /> : <CheckCircle size={15} />}
            <span>{msg.text}</span>
          </div>
        )}

        <form onSubmit={handleSave}>
          {/* Read-Only Locked Fields */}
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px', marginBottom: '16px' }}>
            <div style={{ fontSize: '0.68rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', marginBottom: '6px' }}>
              Verified Entity (Locked by Authority)
            </div>
            <div style={{ fontSize: '0.85rem', fontWeight: 800, color: '#0f172a' }}>{supplier.company_name}</div>
            <div style={{ fontSize: '0.74rem', color: '#64748b', marginTop: '2px' }}>
              GSTIN: <code>{supplier.gst_number}</code> • {supplier.core_offering_category}
            </div>
          </div>

          {/* Editable Fields */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, color: '#064e3b', marginBottom: '4px' }}>
                Contact Person
              </label>
              <input
                type="text"
                value={contactPerson}
                onChange={(e) => setContactPerson(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', borderRadius: '7px', border: '1.2px solid #cbd5e1', fontSize: '0.82rem', boxSizing: 'border-box' }}
                required
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, color: '#064e3b', marginBottom: '4px' }}>
                Mobile Number
              </label>
              <input
                type="text"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', borderRadius: '7px', border: '1.2px solid #cbd5e1', fontSize: '0.82rem', boxSizing: 'border-box' }}
                required
              />
            </div>
          </div>

          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, color: '#064e3b', marginBottom: '4px' }}>
              Storefront / Website URL
            </label>
            <input
              type="url"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              placeholder="https://www.company.com"
              style={{ width: '100%', padding: '8px 10px', borderRadius: '7px', border: '1.2px solid #cbd5e1', fontSize: '0.82rem', boxSizing: 'border-box' }}
            />
          </div>

          <div style={{ marginBottom: '14px' }}>
            <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, color: '#064e3b', marginBottom: '4px' }}>
              Product Catalog & Privilege Offer Synopsis
            </label>
            <textarea
              rows={3}
              value={catalogSummary}
              onChange={(e) => setCatalogSummary(e.target.value)}
              placeholder="Detail discounts, product lines, and warranties provided to privilege members."
              style={{ width: '100%', padding: '8px 10px', borderRadius: '7px', border: '1.2px solid #cbd5e1', fontSize: '0.82rem', boxSizing: 'border-box' }}
              required
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, color: '#064e3b', marginBottom: '4px' }}>
              Update Brand Logo / Emblem
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setLogoFile(e.target.files[0])}
              style={{ fontSize: '0.78rem' }}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%', padding: '11px', background: '#059669', color: '#ffffff',
              fontWeight: 800, border: 'none', borderRadius: '8px', cursor: 'pointer',
              fontSize: '0.84rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
            }}
          >
            <Save size={15} />
            <span>{loading ? 'Saving Changes...' : 'Save Profile Changes'}</span>
          </button>
        </form>
      </div>
    </div>
  );
}